import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { Helmet } from 'react-helmet-async';

export default function GuideCrossFitPage() {
  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const [s1Ref, s1Visible] = useScrollReveal(0.1);
  const [s2Ref, s2Visible] = useScrollReveal(0.1);
  const [s3Ref, s3Visible] = useScrollReveal(0.1);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Qu'est-ce que le CrossFit ? Guide complet pour débutants à Saint-Chamond",
    "description": "Découvrez le CrossFit : origines, bienfaits, déroulement d'une séance, équipement, tarifs. Guide complet par CrossFit Saint-Chamond, votre box dans la Loire.",
    "author": { "@type": "Person", "name": "Nicolas Perroy" },
    "publisher": { "@type": "Organization", "name": "CrossFit Saint-Chamond" },
    "mainEntityOfPage": "https://crossfit-saintchamond.fr/guide-crossfit",
    "datePublished": "2025-01-15",
    "dateModified": "2025-03-30"
  };

  return (
    <div className="min-h-screen">
      <PageMeta
        title="Qu'est-ce que le CrossFit ? Guide complet"
        description="Qu'est-ce que le CrossFit ? Guide complet pour débutants : origines, bienfaits, déroulement d'une séance, équipement, tarifs. CrossFit Saint-Chamond, Loire (42)."
        path="/guide-crossfit"
      />
      <BreadcrumbJsonLd items={[
        { name: 'Accueil', url: '/' },
        { name: 'Guide CrossFit' }
      ]} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <TopBar />
      <Navigation />

      {/* Hero */}
      <section ref={heroRef} className="relative bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_063f6b75-858e-4561-abbf-3c8b3baa3d11/artifacts/3lspxi6i_WhatsApp%20Image%202026-04-02%20at%2014.37.44.jpeg" alt="Salle de CrossFit - entraînement fonctionnel" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/80 to-dark/50" />
        </div>
        <div className="container-custom relative z-10 py-16 md:py-24">
          <div className={`max-w-3xl transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-2 text-white/50 text-sm mb-4">
              <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
              <span>/</span>
              <span className="text-white/70">Guide CrossFit</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl text-white leading-[0.9] mb-6">
              QU'EST-CE QUE LE <span className="text-primary">CROSSFIT</span> ?
            </h1>
            <p className="text-white/60 text-lg max-w-xl">
              Guide complet pour comprendre le CrossFit, ses bienfaits et comment commencer à Saint-Chamond et dans la Vallée du Gier.
            </p>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div ref={s1Ref} className={`mb-16 transition-all duration-700 ${s1Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-heading text-3xl text-dark mb-6">LE CROSSFIT : DÉFINITION ET ORIGINES</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Le <strong>CrossFit</strong> est une méthode d'entraînement créée par Greg Glassman dans les années 2000. Il repose sur des <strong>mouvements fonctionnels variés</strong>, exécutés à <strong>haute intensité</strong>. Contrairement à la musculation traditionnelle qui isole les muscles, le CrossFit travaille le corps dans sa globalité à travers trois piliers : la <strong>gymnastique</strong> (tractions, muscle-ups, handstands), l'<strong>haltérophilie</strong> (snatch, clean & jerk) et le <strong>cardio métabolique</strong> (course, rameur, corde à sauter).
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Le CrossFit est aujourd'hui pratiqué dans plus de <strong>15 000 box affiliées</strong> dans le monde, dont <strong>CrossFit Saint-Chamond</strong> dans la Loire (42). Le terme "box" désigne la salle d'entraînement, souvent un espace brut avec tout l'équipement nécessaire : barres olympiques, kettlebells, rameurs, anneaux de gymnastique, cordes à grimper.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              À <strong>Saint-Chamond</strong>, notre box est également <strong>HYROX Training Club</strong>, ce qui signifie que nous proposons aussi la préparation aux compétitions <strong>HYROX</strong> — un format de fitness combinant courses et exercices fonctionnels qui connaît une explosion de popularité en France et en Europe.
            </p>
          </div>

          <div ref={s2Ref} className={`mb-16 transition-all duration-700 ${s2Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-heading text-3xl text-dark mb-6">LES BIENFAITS DU CROSSFIT</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Le CrossFit développe <strong>10 compétences physiques</strong> : endurance cardiovasculaire, endurance musculaire, force, souplesse, puissance, vitesse, coordination, agilité, équilibre et précision. C'est la méthode d'entraînement la plus complète qui existe.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                'Perte de masse grasse rapide et durable',
                'Gain de force et de masse musculaire',
                'Amélioration de l\'endurance cardio-vasculaire',
                'Meilleure souplesse et mobilité articulaire',
                'Renforcement mental et confiance en soi',
                'Communauté motivante et esprit d\'équipe',
                'Adapté à tous les niveaux (scaling)',
                'Prévention des blessures du quotidien',
              ].map((b, i) => (
                <div key={i} className="flex items-start gap-3 bg-background p-4 rounded-lg">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{b}</span>
                </div>
              ))}
            </div>
            <p className="text-muted-foreground leading-relaxed">
              L'un des grands avantages du CrossFit est le <strong>scaling</strong> : chaque exercice peut être adapté à votre niveau. Un débutant fera des pompes sur les genoux là où un athlète confirmé fera des pompes en poirier. Le résultat : <strong>tout le monde peut faire du CrossFit</strong>, quel que soit son âge ou sa condition physique.
            </p>
          </div>

          <div ref={s3Ref} className={`mb-16 transition-all duration-700 ${s3Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-heading text-3xl text-dark mb-6">COMMENCER LE CROSSFIT À SAINT-CHAMOND</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>CrossFit Saint-Chamond</strong> est situé au <strong>7 Rue Jean-Jacques Rousseau, 42400 Saint-Chamond</strong>. Fondé par <strong>Nicolas Perroy</strong>, coach certifié et passionné, notre box accueille aussi bien les <strong>débutants</strong> que les <strong>athlètes confirmés</strong>.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Nous proposons plusieurs formats d'entraînement : le <strong>WOD</strong> (Workout Of the Day) classique, le <strong>HYROX</strong> (préparation compétition), les <strong>Team WOD</strong> (entraînements en équipe) et le <strong>Free Access</strong> (entraînement libre). Nos cours ont lieu du <strong>lundi au samedi</strong>, avec des créneaux matin, midi et soir.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Notre box est facilement accessible depuis <strong>Saint-Étienne</strong> (12 km), <strong>L'Horme</strong> (3 km), <strong>Rive-de-Gier</strong> (8 km), <strong>La Grand-Croix</strong> (5 km), <strong>Sorbiers</strong>, <strong>La Talaudière</strong>, <strong>Firminy</strong> et toute la <strong>Vallée du Gier</strong>.
            </p>

            <h3 className="font-heading text-xl text-dark mb-4">NOS TARIFS</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { name: '1x/sem', price: '59€/mois' },
                { name: '2x/sem', price: '69€/mois' },
                { name: '3x/sem', price: '89€/mois' },
                { name: 'Illimité', price: '99€/mois' },
              ].map((t) => (
                <div key={t.name} className="bg-background rounded-lg p-4 text-center">
                  <span className="text-xs text-muted-foreground block mb-1">{t.name}</span>
                  <span className="font-heading text-lg text-primary">{t.price}</span>
                </div>
              ))}
            </div>
            <p className="text-muted-foreground text-sm mb-8">
              Sans engagement, préavis de 2 mois. Frais d'adhésion : 99€. Carnets disponibles : 10 séances (149€), 5 séances (79€), drop-in (18€).
            </p>
          </div>

          {/* CTA */}
          <div className="bg-primary rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-3xl text-white mb-4">PRÊT À ESSAYER LE CROSSFIT ?</h2>
            <p className="text-white/80 text-base mb-8 max-w-lg mx-auto">
              Contactez-nous pour votre premier cours d'essai à CrossFit Saint-Chamond.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="w-full sm:w-auto bg-white hover:bg-white/90 text-primary font-heading tracking-wide px-10 py-7 rounded-xl">
                  NOUS CONTACTER <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/programmes">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-primary font-heading tracking-wide px-10 py-7 rounded-xl bg-transparent">
                  NOS PROGRAMMES
                </Button>
              </Link>
            </div>
          </div>

          {/* Internal Links */}
          <div className="mt-16 pt-12 border-t border-border">
            <h3 className="font-heading text-xl text-dark mb-6">EN SAVOIR PLUS</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { to: '/programmes/crossfit-wod', label: 'CrossFit WOD', desc: 'Le Workout Of the Day en détail' },
                { to: '/programmes/hyrox', label: 'HYROX', desc: 'Préparation compétition HYROX' },
                { to: '/programmes/team-wod', label: 'Team WOD', desc: 'Entraînements en équipe' },
                { to: '/planning', label: 'Planning', desc: 'Horaires de tous les cours' },
                { to: '/tarifs', label: 'Tarifs', desc: 'Nos formules et prix' },
                { to: '/about', label: 'Nicolas Perroy', desc: 'Notre fondateur et coach' },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="group block bg-background p-4 rounded-lg hover:bg-accent transition-colors">
                  <span className="font-heading text-sm text-dark group-hover:text-primary transition-colors">{link.label}</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">{link.desc}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
